/**
 * Guest-mode usage quotas — tracked client-side in localStorage.
 *
 * Each feature has a daily limit for unauthenticated users. When the user logs in,
 * limits are removed (the backend has no rate-limit; this is purely a UX gate).
 *
 * Persisted shape:
 *   geovinum.guest.<feature> = { day: 'YYYY-MM-DD', used: <int> }
 */
import { auth } from "./api";

export const GUEST_LIMITS = {
  chat: 5,
  itinerary: 3,
  explorer: 3,
  tasting: 2,
  geocode: 5,
  audrey: 1,
  flashcards: 5,
  play: 5,
};

const PREFIX = "geovinum.guest.";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function _read(feature) {
  try {
    const raw = localStorage.getItem(PREFIX + feature);
    if (!raw) return { day: todayKey(), used: 0 };
    const obj = JSON.parse(raw);
    if (obj.day !== todayKey()) return { day: todayKey(), used: 0 };
    return obj;
  } catch {
    return { day: todayKey(), used: 0 };
  }
}

function _write(feature, obj) {
  try { localStorage.setItem(PREFIX + feature, JSON.stringify(obj)); } catch {}
}

export function isGuest() {
  return !auth.getToken();
}

export function getRemaining(feature) {
  if (!isGuest()) return Infinity;
  const limit = GUEST_LIMITS[feature];
  if (limit == null) return Infinity;
  const used = _read(feature).used;
  return Math.max(0, limit - used);
}

export function canUse(feature) {
  return getRemaining(feature) > 0;
}

/**
 * Consume one quota slot for the given feature.
 * Returns { ok: boolean, remaining: number }.
 */
export function consume(feature) {
  if (!isGuest()) return { ok: true, remaining: Infinity };
  const limit = GUEST_LIMITS[feature];
  if (limit == null) return { ok: true, remaining: Infinity };
  const cur = _read(feature);
  if (cur.used >= limit) return { ok: false, remaining: 0 };
  const next = { day: todayKey(), used: cur.used + 1 };
  _write(feature, next);
  return { ok: true, remaining: Math.max(0, limit - next.used) };
}

export function reset(feature) {
  try { localStorage.removeItem(PREFIX + feature); } catch {}
}
